import { uploadErrorReportContext } from '@flashka/shared/upload/transfer';

import * as Sentry from '@sentry/react';
import { MutationCache, QueryClient } from '@tanstack/react-query';

import {
  httpErrorReportContext,
  shouldReportMutationError,
} from './mutation-error-report';

/**
 * Shared react-query client. Exported from a standalone module (instead of
 * from `src/App.tsx`) so non-React code paths — such as
 * `triggerExploreCompletion` in `src/components/dashboard/explore-toast.tsx`
 * — can invalidate queries without pulling `App.tsx` through a circular
 * import graph.
 */

export const queryClient = new QueryClient({
  // react-query catches mutation rejections internally, so a failed mutation
  // (checkout, deck creation, uploads, sync writes, …) never bubbles to the
  // window's `unhandledrejection` handler and therefore never reaches Sentry's
  // global handlers. Report them centrally here so unexpected mutation
  // failures are observable in prod, regardless of whether the call site
  // wires its own `onError`. Individual mutations can still add an `onError`
  // for user-facing toasts — this only adds observability, it does not
  // suppress anything. Expected flow-control failures are filtered (see
  // `isExpectedMutationError` in ./mutation-error-report), captures are
  // prod-only so dev / e2e sessions never pollute the Sentry project, and a
  // mutation can opt out entirely via `useMutation({ meta: { skipSentry: true } })`
  // — or only for the failures it knows are expected, with a predicate:
  // `meta: { skipSentry: (error) => … }`.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (!import.meta.env.PROD) {
        return;
      }
      if (!shouldReportMutationError(error, mutation.meta)) {
        return;
      }
      // A reportable upload failure is filed by what failed (kind × who
      // answered × status), not by which screen's mutation threw it.
      const upload = uploadErrorReportContext(error);
      // A refused `/api` answer: its reason code and the server's trace id.
      const http = httpErrorReportContext(error);
      Sentry.captureException(error, {
        tags: { source: 'react-query-mutation', ...upload?.tags, ...http?.tags },
        ...(upload ? { fingerprint: upload.fingerprint } : {}),
        ...(http ? { contexts: http.contexts } : {}),
        extra: { mutationKey: mutation.options.mutationKey },
      });
    },
  }),
});
